// Who gets told when the assistant cannot answer a customer.
//
// ─── WHY THIS SCREEN EXISTS ─────────────────────────────────────────────────
//
// Before migration 113 every internal alert went to one field,
// `app_settings.manager_whatsapp`. There was no way to reach a department, and
// two of the six roles had zero people in them — so "give it to Production"
// reached nobody. Numbers had to be added by hand in SQL.
//
// ⚠️ A DEPARTMENT WITH NO NUMBER IS NOT AN ERROR, it falls back to the manager.
// That is deliberate — a missing contact must never mean silence, which was the
// original bug. But it IS worth showing, because a fallback that works quietly
// looks identical to routing that works, and the owner would never learn that
// Production has nobody.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, type RoleWithContacts } from '../../../lib/api';
import { Card, CardHeader } from './_ui';
import { Button } from '../../../components/ui/Button';

/** Roles that can receive an escalation. `roles` also holds Admin and the
 *  relay's own bot account; neither is a department a customer question can be
 *  routed to, and listing them invites somebody to add a number that nothing
 *  will ever use. Must match ROLE_NAME in worker/src/lib/escalationRoute.ts. */
const ROUTABLE = [
  'Agent de confirmation',
  'Éditeur / Design',
  'Production / Impression',
  'Comptabilité / Finance',
];

/** What each department actually receives, in plain French. The owner should
 *  not have to read the routing regexes to know what a number signs up for. */
const WHAT_THEY_GET: Record<string, string> = {
  'Agent de confirmation': 'Tout ce qui n’entre dans aucune autre catégorie, et les demandes générales.',
  'Éditeur / Design': 'Montage, photos manquantes, dédicace, éditeur qui ne s’ouvre pas, aperçu 3D.',
  'Production / Impression': 'Colis, livraison, suivi, livre abîmé, problème d’impression.',
  'Comptabilité / Finance': 'Remboursement, virement, reçu, RIB, facture.',
};

/** Column headings. The full names do not fit five across; the description
 *  each one carries stays on the header's `title`, so nothing is lost. */
const SHORT_NAME: Record<string, string> = {
  'Agent de confirmation': 'Confirmation',
  'Éditeur / Design': 'Design',
  'Production / Impression': 'Production',
  'Comptabilité / Finance': 'Comptabilité',
};

const inputCls = 'w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-[13px] '
  + 'focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500';

export function DepartmentContactsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['role-contacts'],
    queryFn: () => whatsappApi.roleContacts(),
  });

  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['role-contacts'] });

  const add = useMutation({
    mutationFn: (v: { role_id: string; phone: string; label: string | null }) =>
      whatsappApi.addRoleContact(v),
    onSuccess: invalidate,
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      whatsappApi.updateRoleContact(v.id, { enabled: v.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => whatsappApi.deleteRoleContact(id),
    onSuccess: invalidate,
  });

  const roles = (data?.roles ?? []).filter((r: RoleWithContacts) => ROUTABLE.includes(r.name));
  const busy = add.isPending || toggle.isPending || remove.isPending;

  /** Every distinct number across all services — the rows of the grid. */
  const people = [...new Set(roles.flatMap((r: RoleWithContacts) => r.contacts.map((c) => c.phone)))];
  const labelFor = (phone: string) => roles
    .flatMap((r: RoleWithContacts) => r.contacts)
    .find((c) => c.phone === phone && c.label)?.label ?? null;
  const emptyRoles = roles
    .filter((r: RoleWithContacts) => !r.contacts.some((c) => c.enabled))
    .map((r: RoleWithContacts) => r.name);

  return (
    <Card pad={false}>
      <CardHeader
        title="Qui reçoit les conversations bloquées"
        sub="Quand l’assistant ne sait pas répondre, il envoie un résumé sur WhatsApp au service concerné."
      />
      <div className="p-4">
        {isLoading && <p className="text-[12px] text-gray-500">Chargement…</p>}

        {/* ── PEOPLE DOWN, SERVICES ACROSS ──────────────────────────────────
            Four stacked sections repeated the same two numbers four times —
            eight rows to say "both people cover everything". As a grid it is
            two rows, and the thing you actually look for (does any service
            have nobody?) is a glance down a column instead of a scroll.
            ⚠️ An empty column still says so in words underneath: the manager
            fallback works silently, which is exactly why an empty service
            would otherwise never be noticed. ── */}
        {!isLoading && roles.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left text-[11px] font-semibold text-gray-500 pb-2 pr-3">
                    Personne
                  </th>
                  {roles.map((role: RoleWithContacts) => (
                    <th key={role.id} className="pb-2 px-2 align-bottom" title={WHAT_THEY_GET[role.name]}>
                      <span className="block text-[11px] font-semibold text-gray-700 leading-tight">
                        {SHORT_NAME[role.name] ?? role.name}
                      </span>
                    </th>
                  ))}
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {people.map((phone) => (
                  <tr key={phone} className="border-t border-gray-100">
                    <td className="py-2 pr-3 text-[12.5px] text-gray-800 tabular-nums whitespace-nowrap">
                      {phone}
                      {labelFor(phone) && (
                        <span className="text-gray-500"> · {labelFor(phone)}</span>
                      )}
                    </td>
                    {roles.map((role: RoleWithContacts) => {
                      const contact = role.contacts.find((c) => c.phone === phone);
                      return (
                        <td key={role.id} className="py-2 px-2 text-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={Boolean(contact?.enabled)}
                            disabled={busy}
                            aria-label={`${phone} — ${role.name}`}
                            onChange={(e) => {
                              if (contact) toggle.mutate({ id: contact.id, enabled: e.target.checked });
                              else if (e.target.checked) {
                                add.mutate({ role_id: role.id, phone, label: labelFor(phone) });
                              }
                            }}
                          />
                        </td>
                      );
                    })}
                    <td className="py-2 pl-2 text-right">
                      {/* ⚠️ NOT THE SAME AS UNTICKING. A box unticked keeps the
                          record — that is the holiday case. This deletes the
                          person from every service, and says so, because the
                          grid makes the two look interchangeable and they are
                          not. Kept because the old layout had it: this pass
                          moves settings, it does not take any away. */}
                      <button
                        type="button" disabled={busy}
                        onClick={() => {
                          if (!window.confirm(
                            `Retirer ${phone} de TOUS les services ?\n\n`
                            + 'Pour une absence temporaire, décochez les cases : le numéro reste enregistré.',
                          )) return;
                          for (const role of roles as RoleWithContacts[]) {
                            const c = role.contacts.find((x) => x.phone === phone);
                            if (c) remove.mutate(c.id);
                          }
                        }}
                        className="text-[11px] text-gray-400 hover:text-red-600 transition-colors duration-200"
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Services nobody covers. Stated, never inferred from an empty column
            — the fallback to the manager works, so silence would read as fine. */}
        {!isLoading && emptyRoles.length > 0 && (
          <p className="text-[11.5px] text-amber-700 mt-2.5">
            {emptyRoles.map((r) => SHORT_NAME[r] ?? r).join(', ')}
            {' — personne. Ces messages partent au manager.'}
          </p>
        )}

        {/* One add row for the whole grid: a person is added once, then ticked
            into whichever services they cover. Four separate add forms was the
            same field repeated four times. */}
        <div className="mt-3 flex gap-2 items-start border-t border-gray-100 pt-3">
          <input
            className={`${inputCls} max-w-[170px]`} placeholder="06 12 34 56 78"
            value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
          />
          <input
            className={`${inputCls} max-w-[140px]`} placeholder="Nom (optionnel)"
            value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
          />
          <Button
            size="sm" loading={add.isPending} disabled={!newPhone.trim() || busy}
            onClick={() => {
              // Added to Confirmation first — the catch-all service, so a new
              // person is never added into a state where they receive nothing.
              const confirmation = roles.find((r: RoleWithContacts) => r.name === ROUTABLE[0]);
              if (!confirmation) return;
              add.mutate(
                { role_id: confirmation.id, phone: newPhone.trim(), label: newLabel.trim() || null },
                { onSuccess: () => { setNewPhone(''); setNewLabel(''); } },
              );
            }}
          >
            Ajouter
          </Button>
        </div>

        {add.isError && (
          <p className="text-[12px] text-red-600 mt-2">{(add.error as Error).message}</p>
        )}

        <p className="text-[11.5px] text-gray-500 mt-4 leading-snug">
          Le même numéro peut couvrir plusieurs services — il ne recevra qu’un seul message.
          Un service sans numéro n’est jamais ignoré : le message part au manager.
        </p>
      </div>
    </Card>
  );
}
